// BiomeResolver — pure function: camera position → dominant biome
// deps: { tiles, hexKey, pixelToHex, hexSize, getMajorityType, hexesWithinRadius }

import { RING_WEIGHTS } from './constants.mjs';
import { BIOME_IDS } from './ambienceManifest.mjs';

/**
 * Choose sampling radius based on zoom level.
 * Zoomed out (smaller zoom value) → larger radius for broader sampling.
 */
export function radiusFromZoom(zoom) {
  return zoom < 0.7 ? 3 : 2;
}

/**
 * Resolve the dominant biome for the current camera position.
 *
 * @param {{ cameraX: number, cameraY: number, zoom: number }} input
 * @param {{ tiles: Object, hexKey: Function, pixelToHex: Function,
 *           hexSize: number, getMajorityType: Function,
 *           hexesWithinRadius: Function }} deps
 * @returns {{ targetBiome: string|null, confidence: number,
 *             scores: Record<string,number>, radius: number,
 *             centerHex: {q:number,r:number} }}
 */
export function resolveBiome(input, deps) {
  const { cameraX, cameraY, zoom } = input;
  const { tiles, hexKey, pixelToHex, hexSize, getMajorityType, hexesWithinRadius } = deps;

  const centerHex = pixelToHex(cameraX, cameraY, hexSize);
  const radius = radiusFromZoom(zoom);
  const hexes = hexesWithinRadius(centerHex, radius);

  // Accumulate weighted scores per biome
  const scores = {};
  let totalWeight = 0;

  for (const hex of hexes) {
    const key = hexKey(hex.q, hex.r);
    const tile = tiles[key];
    if (!tile || !tile.placed) continue;

    // Ring distance from center
    const dist = (Math.abs(hex.q - centerHex.q)
      + Math.abs(hex.q + hex.r - centerHex.q - centerHex.r)
      + Math.abs(hex.r - centerHex.r)) / 2;
    const weight = RING_WEIGHTS[Math.min(dist, RING_WEIGHTS.length - 1)] || 1;

    const biome = getMajorityType(tile.sides);
    if (biome) {
      scores[biome] = (scores[biome] || 0) + weight;
      totalWeight += weight;
    }
  }

  if (totalWeight === 0) {
    return { targetBiome: null, confidence: 0, scores: {}, radius, centerHex };
  }

  // Normalize scores to [0,1] and find winner
  let bestBiome = null;
  let bestScore = 0;
  for (const biome of BIOME_IDS) {
    if (scores[biome]) {
      scores[biome] /= totalWeight;
      if (scores[biome] > bestScore) {
        bestScore = scores[biome];
        bestBiome = biome;
      }
    }
  }

  return {
    targetBiome: bestBiome,
    confidence: bestScore,
    scores,
    radius,
    centerHex,
  };
}
